import { useCallback, useEffect, useState } from 'react'
import { format, isValid, parse } from 'date-fns'

import { DottedCalendarIcon } from '../../../icons/DottedCalendarIcon'
import { debounce } from '../../../utils/debounce'
import { Calendar } from '../Calendar/Calendar'
import { Dropdown } from '../Dropdown/Dropdown'
import { DropdownCard } from '../Dropdown/DropdownCard'
import { Input } from '../Input/Input'

export interface DateInputProps {
  value?: Date
  onChange: (value?: Date) => void
  placeholder?: string
  hint?: string
  error?: boolean
  dateFormat?: string
  maxDate?: Date
}

export const DateInput = (props: DateInputProps): JSX.Element => {
  const { value, onChange, placeholder, hint, error, dateFormat = 'dd/MM/yyyy', maxDate } = props

  const getInputValue = useCallback(
    (value?: Date): string => {
      return value && isValid(value) ? format(value, dateFormat) : ''
    },
    [dateFormat]
  )

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [inputValue, setInputValue] = useState(getInputValue(value))

  const handleSelect = (value: Date | null) => {
    onChange?.(value || undefined)
    setIsDropdownOpen(false)
  }

  const debouncedChange = useCallback(
    debounce((value: string) => {
      if (value.length !== dateFormat.length) {
        return
      }

      const dateValue = parse(value, dateFormat, new Date())

      if (!isValid(dateValue)) {
        return
      }

      onChange(dateValue)
    }, 650),
    [dateFormat]
  )

  const handleInputChange = (value: string) => {
    setInputValue(value)

    debouncedChange(value)
  }

  useEffect(() => {
    setInputValue(getInputValue(value))
  }, [value, setInputValue, getInputValue])

  return (
    <Dropdown
      direction='bottomRight'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <DropdownCard>
          <Calendar
            key={value?.toString()}
            startDate={value}
            maxDate={maxDate}
            onChange={handleSelect}
            openToDate={value}
          />
        </DropdownCard>
      }
    >
      <Input
        value={inputValue}
        onWrapperClick={(e) => e.stopPropagation()}
        onChange={(e) => handleInputChange(e.target.value)}
        onEndIconClick={() => setIsDropdownOpen(true)}
        placeholder={placeholder}
        endIcon={DottedCalendarIcon}
        hint={hint}
        error={error}
      />
    </Dropdown>
  )
}
